// External Dependencies
import React, { Fragment } from 'react';
import Amplify from 'aws-amplify';
import { Route, Switch, Redirect, withRouter } from 'react-router-dom';
import awsmobile from '../aws-exports.js';
import cx from 'classnames';
import PropTypes from 'prop-types';

// Internal Dependencies
import ReaderManager from './Reader/ReaderManager';
import StudentHome from './Student/StudentHome';
import Speech from './Reader/Speech/Speech';
import Coach from './Coach/Coach';
import Navigation from './Navigation';
import Menu from './Menu';
import {
  getPaginatedStory,
  getSquashedStory,
  getAssessmentStory,
  getUserData,
  setUserData,
  checkForAnyCompletedActivity
} from '../services/API';
import studentRecordService from '../services/StudentRecordService';
import Modal from './Modal';
import NativeReports from './Student/NativeReports/NativeReports';
import { RouteComponentRenderer, routeStore } from '../routes';
import config from '../config';
import {assignmentTypes,userTypes} from '../constants/constants';
import {COPY_STRINGS, setCopyStrings} from '../constants/translatedCopy';
import { chatbotName } from './ChatBot/ChatBot';
import SpeechButton from './SpeechButton';
import {ErrorModal, getErrorModalTypes} from './Reader/ErrorModal';
import StudentHistory from './Student/StudentHistory';
import HelpButton from './Teacher/HelpButton';
import Legend from './Teacher/Legend';
import {legendItems, dyslexiaRiskLegendItems} from '../constants/legend';
import silence from "../images/silence.mp3";
import {isMicAllowed} from "../services/environment"
import device from '../services/device/device.js';
import LanguageSettings from './Settings/LanguageSettings';
import {logSessionInfo} from '../services/logger';
import ChatSupport from "Components/Teacher/ChatSupport";
import EmailCaptureModal from "./Teacher/EmailCaptureModal";
import {getAssignmentForStudent,} from "Services/assignment/getAssignment/getAssignment";
import {getPendingManifest} from "Services/assignment/functions";
import {getAssignmentConfiguration} from "Services/assignment/manifest.js";
import VirtualScreen from './Reader/VirtualScreen/VirtualScreen.js';
import {ASSIGNMENT_SEQUENCE} from "Services/assignment/constants";
import Loading from "./Global/Loading";
import {StudentPreChecks, READY_MODULE_TEST_MODES, StudentASRReaderWrapper} from './StudentPreChecks/index';
import StudentInformationService from '../services/StudentInformationService.js';
import { getCalibrationAssignment, getScreenerOverrides } from '../services/calibrationForms.js';
import {fetchBadges} from '../services/badging/fetchBadges/fetchBadges';
import { getLanguageForLocale } from '../services/locale.js';
import { CurrentStoryWordMetadataProvider } from '../context/CurrentStoryMetadataContext.js';
import { GetBrandedAppName, GetBrandedTutorName } from '../services/BrandingService.js';
import ReaderManagerErrorBoundary from './Reader/ReaderManagerErrorBoundary.js';

//Note - this is meant to be persistent throughout the entire
// login session so do NOT clear it ever.
let activityStack = [];
let activityObjectStack = [];

export function getActivityStack(){
  return activityStack;
}

export function getActivityObjectStack(){
  return activityObjectStack;
}

export function addToActivityStack(activity){
  activityStack.push(activity);
}

export function removeLastActivityFromStack(){
  return activityStack.pop();
}

Amplify.configure(Object.assign({}, awsmobile, {
  Interactions: {
    bots: {
      [chatbotName]: {
        name: chatbotName,
        alias: "$LATEST",
        region: "us-east-1",
      },
    }
  },
  Auth: {
    'authenticationFlowType': 'USER_PASSWORD_AUTH',
  }
}));

class TeacherRoutes extends React.Component {
  render() {
    //These routes are only available to teachers
    if(this.props.userType == userTypes.TEACHER){
      return (
        <Switch>
          {routeStore.teacher.routes.map((routeConfig) =>
            <RouteComponentRenderer key={routeConfig.path} {...routeConfig} {...this.props} />
          )}
          <Route render={(props) => <Redirect to={routeStore.teacher.DEFAULT} {...props} />} />
        </Switch>
      );
    }
    return null;
  }
}

class SchoolAdminRoutes extends React.Component {
  render() {
    // These routes are only available to admins
    // Note that admins currently share identical routes
    if(this.props.userType == userTypes.SCHOOL_ADMIN){
      return (
        <Switch>
          {routeStore.SCHOOL_ADMIN.routes.map((routeConfig) =>
            <RouteComponentRenderer key={routeConfig.path} {...routeConfig} {...this.props} />
          )}
          <Route render={(props) => <Redirect to={routeStore.SCHOOL_ADMIN.DEFAULT} {...props} />} />
        </Switch>
      );
    }
    return null;
  }
}

class DistrictAdminRoutes extends React.Component {
  render() {
    // These routes are only available to admins
    // Note that admins currently share identical routes
    if(this.props.userType == userTypes.DISTRICT_ADMIN){
      return (
        <Switch>
          {routeStore.DISTRICT_ADMIN.routes.map((routeConfig) =>
            <RouteComponentRenderer key={routeConfig.path} {...routeConfig} {...this.props} />
          )}
          <Route render={(props) => <Redirect to={routeStore.DISTRICT_ADMIN.DEFAULT} {...props} />} />
        </Switch>
      );
    }
    return null;
  }
}

class StudentRoutes extends React.Component {
  render() {
    if(this.props.userType == userTypes.STUDENT && !this.props.forcedAssessment){
      return (
        <Switch>
          {this.props.children}
        </Switch>
      );
    }
    return null;
  }
}

class ForcedAssessment extends React.Component {
  render() {
    if(this.props.userType == userTypes.STUDENT && this.props.forcedAssessment){
      return (
        <Switch>
          {this.props.children}
        </Switch>
      );
    }
    return null;
  }
}

class App extends React.Component {
  constructor (props){
    super(props);
    this.currentPathname = null;
    this.currentSearch = null;

    this.state = {
      // user
      paginatedStory: null,
      pageIndex: 0,
      assignmentIndex: 0,
      activityIndex: 0, // a count of activity during an app session, used for presenting introductions between full and short
      coachMessage: {id: null, text: "Let's get started!", texttoread: null},
      interventions: {},    // an object containing our intervention functions, will be set via callback by Reader
                            // TODO: eventually we may want to add interventions from multiple parts of the app, not just reader. When that time comes, adjust this structure accordingly
      coachReady: getUserData().sessionData.isiPad || this.props.noAvatar, // if we're on iPad we're not going to load the coach, so we don't have to wait for it to be ready
      modal: null,
      loading: true,
      preChecksDone: getUserData().userType === userTypes.STUDENT ? false : true,
      ready: this.shouldByPassStudentSetup(),
      initialUserInteraction: false,
      isReaderActive: getUserData().userType == userTypes.STUDENT ? true : false,//ChatBot will only  be available for Teachers
      forcedAssessment: null,
      orfMode: true,  //TODO: refactor when assignments carry the assignmentType information, and when ORF uses the same technique as other assignments
      error: false,
      readerKey: 0,
      midAssessment: false,   //whether or not we are currently in the middle of a not-yet-complete assessment
      micEnabled: false,
      micChecked: false,
      hideReaderContainer: true,
      readyModuleTestMode: this.getReadyModuleTestModes(),
      forcedPhrasesPerPage: this.getForcedPhrasesPerPage(),
      animationToPlay: null,
      readyModal: false,
    };

    this.nextPage = this.nextPage.bind(this);
    this.previousPage = this.previousPage.bind(this);
    this.setStory = this.setStory.bind(this);
    this.resetStory = this.resetStory.bind(this);
    this.resetForcedStory = this.resetForcedStory.bind(this);
    this.setCoachMessage = this.setCoachMessage.bind(this);
    this.setInterventions = this.setInterventions.bind(this);
    this.coachReadyCallback = this.coachReadyCallback.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.muteReader = this.muteReader.bind(this);
    this.unMuteReader = this.unMuteReader.bind(this);
    this.clearAssignment = this.clearAssignment.bind(this);
    this.setMidAssessment = this.setMidAssessment.bind(this);
    this.logTimeOnStoryStart = this.logTimeOnStoryStart.bind(this);
    this.logTimeOnStoryEnd = this.logTimeOnStoryEnd.bind(this);
    this.saveSessionTime = this.saveSessionTime.bind(this);
    this.checkAssignments = this.checkAssignments.bind(this);
    this.nextAssignment = this.nextAssignment.bind(this);
    this.forceAssignment = this.forceAssignment.bind(this);
    this.setParentActivityId = this.setParentActivityId.bind(this);
    this.openReadyModal = this.openReadyModal.bind(this);
    this.onPreCheckDone = this.onPreCheckDone.bind(this);
    this.toggleReaderContainer = this.toggleReaderContainer.bind(this);
    this.onStartButtonClicked = this.onStartButtonClicked.bind(this);
    this.onDonePlayingBootstrap = this.onDonePlayingBootstrap.bind(this);
    this.stageElement = React.createRef(); // Reference to the stage element added to pass pixi app down to subcomponents
    this.switchReaderContainerBackground = this.switchReaderContainerBackground.bind(this);


    //TODO: This shouldn't be necessary after integrating with Router
    this.useNav = this.props.config.useNav;
    this.useMenu = this.props.config.useMenu;

    const session = getUserData();
    const isStudent = session.userType == userTypes.STUDENT
    this.useCoach = !this.props.noAvatar && isStudent

    this.didGetAssignmentForStudent = false

    this.totalReadingTime = {
      start: null,
      end: null,
    };

    this.setBadges();

    props.setPreChecksDone(this.state.preChecksDone);
  }

  componentDidMount(){
    // set up Google Analytics with React Router (NOTE: Temporarily disabled)
    const { history } = this.props;

    // Eat the back button (source: https://subwaymatch.medium.com/disabling-back-button-in-react-with-react-router-v5-34bb316c99d7)
    history.listen((newLocation, action) => {
      if(getUserData().userType !== userTypes.STUDENT) return;

      if (["PUSH","REPLACE"].includes(action)) {
        if (
          newLocation.pathname !== this.currentPathname ||
          newLocation.search !== this.currentSearch
        ) {
          // Save new location
          this.currentPathname = newLocation.pathname;
          this.currentSearch = newLocation.search;

          // Clone location object and push it to history
          history.push({
            pathname: newLocation.pathname,
            search: newLocation.search
          });
        }
      } else {
        // Send user back if they try to navigate back
        history.go(1);
      }
    }).bind(this);

    let session = getUserData();
    //set the locale
    setCopyStrings(session.locale);

    // check compatibility
    if(device.browser){
      console.log("Browser:", device.getBrowserForSession());
      logSessionInfo("device_information",{
        windowSize: {
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio
        },
        userAgent: window.navigator.userAgent,deviceInfo:
        device
      });

      if(device.isMobilePlatform){
        //###Leave this commented out for now, but restore once intermittent bug is resolved
        /*this.setState({error: true, ready: false});
        this.openModal(
          <ErrorModal
            type={getErrorModalTypes().COMPAT_MOBILE}
          />
        );
        return;*/
        logSessionInfo("incompatible_mobileplatform_detected",{userAgent: window.navigator.userAgent,deviceInfo: device});
      }
      if(!device.hasCompatibleOS){
        this.setState({error: true, ready: false});
        this.openModal(
          <ErrorModal
            type={getErrorModalTypes().COMPAT_OS}
          />
        );
        logSessionInfo("incompatible_os_detected",{userAgent: window.navigator.userAgent,deviceInfo: device});
        return;
      }
      if(!device.hasCompatibleBrowser){
        this.setState({error: true, ready: false});
        this.openModal(
          <ErrorModal
            type={getErrorModalTypes().COMPAT}
          />
        );
        logSessionInfo("incompatible_browser_detected",{userAgent: window.navigator.userAgent,deviceInfo: device});
        return;
      }
      if(getUserData().sessionData.isiPad && parseFloat(device.browser.version) < 12){
        this.setState({error: true, ready: false});
        this.openModal(
          <ErrorModal
            type={getErrorModalTypes().COMPAT_IPAD}
          />
        );
        logSessionInfo("incompatible_iosversion_detected",{userAgent: window.navigator.userAgent,deviceInfo: device});
        return;
      }
    }
    if(getUserData().sessionData.isiPad && getUserData().userType == userTypes.TEACHER){
      this.setState({error: true, ready: false});
      this.openModal(
        <ErrorModal
          type={getErrorModalTypes().IPAD}
        />
      );
      return;
    }

    // prevent deep link to activity page
    if(location.pathname.includes("scoring")) {
      this.props.history.push("/teacher");
    }

    this.setState({
      readyModal: true,
    }) // open ready modal, usually in loading state.

    if(session.userType === userTypes.STUDENT) {
      this.checkAssignments(session);
    }
  }

  componentDidUpdate(previousProps, previousStates){
    if(this.state.preChecksDone && !this.state.ready){
      this.setState({
        ready: true
      })
    }
  }

  getReadyModuleTestModes(){
    const urlParams = new URLSearchParams(window.location.search);
    const mode = this.props.skipIntros ? READY_MODULE_TEST_MODES.SKIP : urlParams.get('readyModuleTestMode')

    if([READY_MODULE_TEST_MODES.SKIP,
        READY_MODULE_TEST_MODES.FTUE,
        READY_MODULE_TEST_MODES.RETURNED_USER_WITH_ASSESSMENTS,
        READY_MODULE_TEST_MODES.RETURNED_USER].includes(mode)){
      return mode
    }
    return null
  }

  getForcedPhrasesPerPage(){
    const urlParams = new URLSearchParams(window.location.search);
    const phrasesPerPage = urlParams.get('phrasesPerPage')

    if(phrasesPerPage){
      return phrasesPerPage
    }
    return null
  }

  shouldByPassStudentSetup(){
    const userData = getUserData()
    const shouldByPass = [userTypes.TEACHER, userTypes.DISTRICT_ADMIN, userTypes.SCHOOL_ADMIN].includes(userData.userType)
    return shouldByPass
  }

  setLocale(locale){
    setUserData("locale", locale);

    //set html lang attribute for accessibility
    document.documentElement.lang = getLanguageForLocale(locale);

    this.setBilingualMode();
  }

  setBilingualModeLocale(bilingualLocale){
    setUserData("bilingualLocale", bilingualLocale);
    this.setBilingualMode();
  }

  setBilingualMode(){
    const { locale, bilingualLocale } = getUserData();
    const bilingualMode = (bilingualLocale && bilingualLocale !== locale);
    setUserData("bilingualMode", bilingualMode);
    setCopyStrings(bilingualMode ? bilingualLocale : locale);
  }

  switchReaderContainerBackground(current, newBG) {
    if(current) document.querySelector(".readerContainer").classList.remove(current);
    if(newBG) document.querySelector(".readerContainer").classList.add(newBG);
  }

  async setBadges(){
    const session = getUserData();
    if(session.badgingEnabled){
      const badges = await fetchBadges([session.userId]);
      setUserData("badges", badges);
    }else{
      setUserData("badges", []);
    }
  }

  toggleReaderContainer() {
    this.setState(prevState => ({
      hideReaderContainer: !prevState.hideReaderContainer
    }));
  }

  forceAssignment(){
    //This will always force a single ORF assignment
    let locale = getUserData().isESL ? "es-mx" : "en-us";
    this.setState({
      forcedAssessment: [{
        "activityType": "assessment",
        "locale": locale,
        "status": "ASSIGNED",
        "sequence": "CONTINUE"
      }],
      orfMode: true
    });
  }

  setParentActivityId(newParentActivityId){
    this.setState({parentActivityId: newParentActivityId});
  }


  async onStartButtonClicked(speechProps){
    // Bootstrap the app by playing a silent sound. This is to get around the
    // fact that iOS does not allow playing sounds without user interaction.
    await speechProps.playAppSoundsWithPromise({
      url: silence,
      key: 'silence',
      onfinish: this.onDonePlayingBootstrap,
    });
  }

  onDonePlayingBootstrap(){
    this.setState({
      readyModal: false,
      initialUserInteraction: true,
    }, () => {
      this.props.history.push('/student/pre-checks')
    })
  }

  onLanguageSettingsClose(locale){
    this.setBilingualModeLocale(locale);
    this.setState({
      readyModal: true,
    }) // open ready modal, usually in loading state.
  }

  openReadyModal(speechProps){
    document.title = `Ready to Start - ${GetBrandedAppName()}`;

    // isMicAllowed().then((isMicAllowed) => {
      // this.setState({micEnabled: isMicAllowed, micChecked: true});
      const buttonClasses = cx({
        "startButton": true,
        "disabled": !this.state.coachReady || this.state.loading,
        "loading": !this.state.loading,
      })
      const buttonText = this.state.loading ? COPY_STRINGS.loading : COPY_STRINGS.lets_do_it
      if(!this.state.ready && !this.state.error){
        return (
          <div className="modalBackground" tabIndex="-1">
            <div className="modalContainer" tabIndex="-1">
              <div className="readyModal">
                <span className="labelFontOverride">{COPY_STRINGS.ready_to_start}</span>
                {this.state.coachReady && !this.state.loading ?
                  <button className="startButton" onClick={() => {this.onStartButtonClicked(speechProps)}}>{COPY_STRINGS.lets_do_it}</button>
                  : <div className="startButton disabled">{COPY_STRINGS.loading} <Loading inline autoFit/></div>}
              </div>
            </div>
          </div>
        );
      }
    // });
  }


  // allows us to pass a custom close function to the modal. This is used to pass in
  // NULL if we want to close the modal via an internal button instead of the built in close feature
  openModal(content, props={}){
    let modal = (
      <Modal
        closeModal={props.close}
        delayedCloseButton={props.delayedCloseButton || false}
        delayedCloseButtonTimeoutOverride={props.delayedCloseButtonTimeoutOverride}
        closeButton={props.closeButton || false}
        backgroundClose={props.backgroundClose || false}
        key={props.key}
        hideBackground={props.hideBackground || false}>
        {content}
      </Modal>
    );

    this.setState({
      modal: modal,
    });
  }

  closeModal(onModalClose=null){
    this.setState({
      modal: null,
    }, onModalClose);
  }

  nextPage(callback=null){
    if(!this.state.paginatedStory || !this.state.paginatedStory.pages) return;
    if(this.state.pageIndex < this.state.paginatedStory.pages.length - 1){
      this.setState({
        pageIndex: this.state.pageIndex + 1,
      }, callback);
    }
  }

  previousPage(){
    if(this.state.pageIndex > 0){
      this.setState({
        pageIndex: this.state.pageIndex - 1,
      });
    }
  }

  setInterventions(interventions){
    this.setState({
      interventions: interventions,
    });
  }

  resetForcedStory(){
    this.setState({
      forceStory: null,
      studentAssignment: null,
    });
  }

  resetStory(){
    this.setState({
      activityIndex: this.state.activityIndex + 1, // increment the index each time an activity is finished
      paginatedStory: null,
      pageIndex: 0,
      readerKey: this.state.readerKey + 1,  // increment the reader key each time we reset the story to ensure that we wipe the component state clean as well as simply wiping the story away
    });
  }

  saveSessionTime() {
    const { activityId } = getUserData();
    const timeSpent = this.totalReadingTime.end - this.totalReadingTime.start;

    studentRecordService.saveSessionTime(activityId, timeSpent);
  }

  //Log the time when the story finishes
  logTimeOnStoryEnd(){
    this.totalReadingTime.end = new Date();
    this.saveSessionTime();
  }

  //Log the time when the story loads
  logTimeOnStoryStart(){
    this.totalReadingTime.start = new Date();
  }

  //pulls a story based on ID, paginates that story, and sets it for the reader
  setStory(id, config, callback=null){
    document.title = config.assessmentMode ? `Student Assessment - ${GetBrandedAppName()}` : `Tutor Story - ${GetBrandedAppName()}`;

    if(config.assessmentMode){ // get the story in assessment format if this is assessment mode
      getAssessmentStory(id).then((paginatedStory) => {
        this.setState({paginatedStory}, callback);
      });
    } else if(config.squashedStory){  //squash the story if necessary
      getSquashedStory(id).then((paginatedStory) => {
        this.setState({paginatedStory}, callback);
      });
    } else {

      let phrasesPerPage = 3;
      if (window.innerHeight <= 768) {
        phrasesPerPage = 1;
      }
      if (this.state.forcedPhrasesPerPage) {
        phrasesPerPage = this.state.forcedPhrasesPerPage;
      }

      getPaginatedStory(id, phrasesPerPage).then((paginatedStory) => {
        this.setState({paginatedStory}, callback);
      });
    }
  }

  setMidAssessment(){
    //there doesnt need to be a way to turn off this flag because we log out at the end of an assessment. if this changes, change this
    this.setState({midAssessment: true});
  }

  checkAssignments(session){
    // check whether or not there is a current pending assignment for this student
    getAssignmentForStudent(session.userId, session).then(async (assignment) => {
      // check for a calibration type and transform if so. TODO: have the backend handle this in the future
      if(assignment && assignment.assignmentType === "CALIBRATION" || this.props.forceCalibration){
        console.log("Overriding calibration assignment")
        const {actualGrade, sisGrade} = getUserData();
        const gradetoUse = sisGrade ? sisGrade : actualGrade;
        assignment = await getCalibrationAssignment(gradetoUse,assignment,this.props.forceCalibration);
        setUserData("screenerOverrides", getScreenerOverrides(assignment));
        this.setLocale('en-us');
        this.setBilingualModeLocale('en-us');
        this.setBilingualMode();
      }

      console.log("ASSIGN",assignment);

      const forcedMode = session.demoMode || this.props.forceTutor || this.props.forceStory || this.props.forceIntervention;
      if ((assignment && assignment.assignmentType && assignment.assignmentType.toLowerCase() !== assignmentTypes.TUTOR) && (session.assessmentEnabled || session.screenerEnabled) && !forcedMode) {
        const assignmentConfig = getAssignmentConfiguration(assignment);
        setUserData('assignmentConfiguration', assignmentConfig);
        const remainingManifest = await getPendingManifest(assignment.manifest);
        logSessionInfo("assignment_manifest",{remainingManifest});
        if (remainingManifest.length) {
          //rollup all manifest activities to be compatible with the activity parenting logic
          assignment.manifest.forEach(item => {
            const {activity} = item;
            if (activity && activity.length) {
              activityStack = activityStack.concat(activity.map(a => a.activityId));
              activityObjectStack = activityObjectStack.concat(activity);
            }
          });

          const nextManifestItem = remainingManifest[0];

          // need to find the assessment id as the parent activity id
          let parentActivityId = null;
          if(activityObjectStack.length){
            parentActivityId = activityObjectStack.find(a => a.type === "assessment")?.activityId || activityObjectStack[activityObjectStack.length - 1].activityId;
          }

          this.setState({
            loading: false,
            studentAssignment: assignment,
            forcedAssessment: remainingManifest,
            parentActivityId: parentActivityId,
            orfMode: nextManifestItem.type === assignmentTypes.ASSESSMENT,
          });
        } else {
          this.setState({
            loading: false
          })
          if (session.userType === userTypes.STUDENT) {
            this.props.history.push("/student/home");
          }
        }
      } else {
        const anyCompleted = await checkForAnyCompletedActivity(session.userId);
        const forceTutor = session.tutorEnabled && !anyCompleted && !session.isEarlyReader;
        setUserData('forceTutor', forceTutor);

        if(anyCompleted){
          StudentInformationService.setFTUESeen(session.userId, true)
          setUserData('hasSeenFTUE', true);
        }

        let forceStory = null;
        let studentAssignment = null;
        if(assignment && assignment.assignmentType && assignment.assignmentType.toLowerCase() === assignmentTypes.TUTOR){
          const tutorManifest = await getPendingManifest(assignment.manifest);
          forceStory = tutorManifest && tutorManifest.length > 0 ? tutorManifest[0].storyId : null;
          studentAssignment = assignment;
          console.log("forcing tutor assignment story", forceStory);
        }

        if (session.userType === userTypes.STUDENT) {
          this.props.history.push("/student/home");
        }
        this.setState({
          loading: false,
          forceStory: forceStory,
          studentAssignment: studentAssignment,
        })
      }
      this.setState({
        readyModal: true,
      }) // open ready modal, usually in loading state. // open a second time so that it can refresh the loading state in the modal UI
    });
  }

  nextAssignment(){
    const {forcedAssessment, assignmentIndex} = this.state;
    const currentAssignedActivity = forcedAssessment[assignmentIndex];
    const nextAssignedActivityIndex = assignmentIndex + 1;
    const nextAssignedActivity = forcedAssessment[nextAssignedActivityIndex];

    this.setState({
      assignmentIndex: nextAssignedActivityIndex,
      bifurcatedAssignment: nextAssignedActivity && (currentAssignedActivity.sequence === ASSIGNMENT_SEQUENCE.BREAK),
      orfMode: nextAssignedActivity && nextAssignedActivity.type === assignmentTypes.ASSESSMENT,
    });
  }

  getCoachMessage(){
    if(this.state.coachMessage){
      return this.state.coachMessage;
    } else if(this.state.pageIndex == 0){
      return ({id: Date.now(), text: "Let's get started!", texttoread: null});
    } else if(this.state.paginatedStory && this.state.paginatedStory.pages && (this.state.pageIndex >= this.state.paginatedStory.pages.length - 1)){
      return ({id: Date.now(), text: "Great job! You finished the story!", texttoread: "Great job! You finished the story"});
    } else {
      return null;
    }
  }

  coachReadyCallback(inState){
    this.setState({
      coachReady: inState,
      readyModal: true,
    });
  }

  setCoachMessage(message){
    this.setState({
      coachMessage: {
        text: message.text || message,
        pronunciation: message.pronunciation || message.text || message,
        callback: message.callback,
        id: this.state.coachMessage ? this.state.coachMessage.id + 1 : 0,
        skipAvatar: (!this.useCoach) || (!!message.skipAvatar),
        passThroughSpeechTags: message.passThroughSpeechTags ? true : false,
      },
    });
  }

  // for now this does nothing because we want students to be permanently stuck in forced assessment mode for MVP.
  // Once we have real assignments, and want to provide access to the rest of the student experience,
  // this function will reset the forcedAssessment state and will call an API function to mark the assignment as completed
  clearAssignment(){
    return null;
  }

  //Don't let ChatBot receive a transcript,
  //instead channel the transcript to ReaderManager
  unMuteReader(){
    this.setState({
      isReaderActive: true,
    });
  }

  //Channel the transcript to ChatBot
  muteReader(){
    this.setState({
      isReaderActive: false,
    });
  }

  // display menu only for tutor mode
  getMenu(speechProps){
     let renderMenu = () => {
      return (
        <div className="menuContainer">
          <Menu
            interventions={this.state.interventions}
            onOpen={speechProps ? speechProps.stopListening : null}
            onClose={speechProps ? speechProps.startListening : null}
          />
        </div>
      );
    };
    return(
      <Route exact path='/student/tutor' render={renderMenu} />
    );
  }

  // display nav only for tutor mode
  getNav(){
    let renderNav = () => {
      return (
        <div className="NavigationContainer">
          <Navigation next={this.nextPage} previous={this.previousPage}/>
        </div>
      );
    };
    return(
      <Route exact path='/student/tutor' render={renderNav} />
    );
  }

  renderStudentHome(routerProps, speechProps){
    //it's easier and cleaner to pass this info through as props at this point, for this piece
    let session = getUserData();

    return (
      <StudentHome
        resetStory={this.resetStory}
        closeModal={this.closeModal}
        openModal={this.openModal}
        sendMessage={this.setCoachMessage}
        speech={speechProps}
        studentName={session.firstName}
        studentId={session.userId}
        session={session}
        setLocale={this.setLocale.bind(this)}
        {...routerProps}
      />
    );
  }

  renderStudentHistory(routerProps,speechProps){
    const session = getUserData();
    return (
      <StudentHistory
        setLocale={this.setLocale.bind(this)}
        speech={speechProps}
        session={session}
        switchReaderContainerBackground={this.switchReaderContainerBackground}
        openModal={this.openModal}
        return='/student/home'
        {...routerProps}
      />
    );
  }

  renderStudentReports(routerProps){
    return (
      <NativeReports
        return='/student/home'
        {...routerProps}
      />
    );
  }

  onPreCheckDone(){
    this.resetStory()
    this.setState({
      preChecksDone: true
    })
    this.props.setPreChecksDone(true);
  }

  getReader(routerProps, speechProps, transcript, readerConfig=this.props.config){
    //TODO: passing test through here is a hack, which will be solved when we solve the config propogation bug
    return (
      <CurrentStoryWordMetadataProvider>
        <ReaderManagerErrorBoundary openModal={this.openModal}>
        <ReaderManager
          key={"readerInstance" + this.state.readerKey}    //this will force a fresh new component when the story changes. This way we wont accidentally have any old kruft holding over from previous sessions
          asrStoryID={!this.state.preChecksDone ? routerProps.asrStoryId : ""}
          preChecksDone={this.state.preChecksDone}
          forceDialogFTUE={this.props.forceDialogFTUE}
          isTest={readerConfig.test}
          modal={this.state.modal}
          config={routerProps.config || readerConfig}
          story={this.state.paginatedStory}
          title={this.state.paginatedStory ? this.state.paginatedStory.title : null}
          author={this.state.paginatedStory ? this.state.paginatedStory.author : null}
          brand={this.state.paginatedStory ? this.state.paginatedStory.brand : null}
          questions={this.state.paginatedStory ? this.state.paginatedStory.questions : null}
          items={this.state.paginatedStory ? this.state.paginatedStory.items : null}
          sendMessage={this.setCoachMessage}
          muteReader={this.muteReader}
          unMuteReader={this.unMuteReader}
          speech={speechProps}
          isQuiet={speechProps.isQuiet}
          numPhonemes={speechProps.numPhonemes}
          abPhonemes={speechProps.abPhonemes}
          transcript={this.state.isReaderActive ? transcript : {}}
          isReaderActive={this.state.isReaderActive}
          setInterventions={this.setInterventions}
          setStory={this.setStory}
          resetForcedStory={this.resetForcedStory}
          resetStory={this.resetStory}
          coachIsReady={this.state.coachReady}
          pageIndex={this.state.pageIndex}
          openModal={this.openModal}
          closeModal={this.closeModal}
          goToNextPage = {this.nextPage}
          studentAssignment={this.state.studentAssignment}
          forcedAssessment = {this.state.forcedAssessment && this.state.forcedAssessment[this.state.assignmentIndex]}
          allAssignments={this.state.forcedAssessment}
          assignmentIndex={this.state.assignmentIndex}
          parentActivityId = {this.state.parentActivityId}
          setParentActivityId = {this.setParentActivityId}
          lastAssignment = {this.state.forcedAssessment && (this.state.assignmentIndex >= this.state.forcedAssessment.length - 1)}
          bifurcatedAssignment = {this.state.bifurcatedAssignment}
          firstAssignment = {this.state.assignmentIndex == 0}
          activityIndex = {this.state.activityIndex}
          clearAssignment = {this.clearAssignment}
          selectStories = {this.props.selectStories}
          forceStory = {this.props.forceStory ? this.props.forceStory : this.state.forceStory}
          forceIntervention = {this.props.forceIntervention}
          interventionBias = {this.props.interventionBias}
          midAssessment = {this.state.midAssessment}
          setMidAssessment = {this.setMidAssessment}
          logTimeOnStoryStart={this.logTimeOnStoryStart}
          logTimeOnStoryEnd={this.logTimeOnStoryEnd}
          orfMode = {this.state.orfMode}
          nextAssignment = {this.nextAssignment}
          forceAssignment={this.forceAssignment}
          setLocale={this.setLocale.bind(this)}
          skipIntros = {this.props.skipIntros}
          forceInterventionSelectionGrade={this.props.forceInterventionSelectionGrade}
          forceInterventionSelectionWCPM={this.props.forceInterventionSelectionWCPM}
          forceInterventionSelectionARM={this.props.forceInterventionSelectionARM}
          returnUrl={this.props.returnUrl}
          {...routerProps}
        />
        </ReaderManagerErrorBoundary>
      </CurrentStoryWordMetadataProvider>
    );
  }

  render() {
    //If the microphone is not enabled, all we can/should show is the modal
    // if(!this.state.micChecked) return(this.state.modal);

    const { userType, partner, branding } = getUserData();

    /*
    * REDIRECTS
    * Handle initial redirect if necessary.
    * Doing this here rather than on inner route render so that surrounding styles don't get lost on loading screen
    */

    //Evaluate these bools here to make the code more readable below.
    let redirectToActivities = false;
    let redirectToAssessment = false;
    let redirectToHome = false;

    // Use if-else so we don't get multiple redirects. The way it worked before this was returning the redirects
    // directly, so there was an implicit priority. We need to put the redirects down in the <Speech> structure
    // rather than directly returning to prevent the whole tree from being torn down and rebuilt.
    if(this.state.ready){
      if(userType == userTypes.TEACHER && !this.props.location.pathname.includes('teacher') && !getUserData().reportingRole){
        redirectToActivities = true;
      }else if(userType == userTypes.STUDENT && this.state.forcedAssessment && !this.props.location.pathname.includes('assessment')){
        redirectToAssessment = true;
      }
    }else{
      if(userType == userTypes.STUDENT && !this.props.location.pathname.includes('student')){
        redirectToHome = true;
      }
    }
    /*******************************************************************/

    let isTeacher = this.props.location.pathname.includes('teacher') || this.props.location.pathname.includes('admin');
    let paperStyle = !isTeacher
    let readerContainerClasses = cx({
      'readerContainer': true,
      'reportBox' : !paperStyle,
      'fullScreen': !this.useCoach
    });

    let readerClasses = cx({
      'reader': true,
      'paper' : paperStyle,
    });

    let pixiStageClass = cx({
      'appLevelPixiStage': true,
      'active': this.state.animationToPlay, // used to move the z index to put the animation on top
    });



      return (
        <Speech config={this.props.config} forceKaldi={!this.state.orfMode && userType == userTypes.STUDENT} skipAvatar={!this.useCoach} forceGooglePM={ this.props.forceGooglePM}>
        {(speechProps, transcript) =>
          <Fragment>
            <div className="app">
              {redirectToActivities &&
                <Route render={(props) => <Redirect to='/teacher/gradebook/activities' {...props} />} />
              }
              {redirectToAssessment &&
                <Route render={(props) => <Redirect to='/student/assessment' {...props} />} />
              }
              {redirectToHome &&
                <Route render={(props) => <Redirect to='/student/home' {...props} />} />
              }
              {this.state.readyModal && this.openReadyModal(speechProps)}
              {/*app level stage to display pixi animations that it's ok to remove from the tree, or pure pixi routes*/}
              {<div className={"rightPanel" + (!this.useCoach ? " hidden" : "")}>
                <Coach
                  config={this.props.config}
                  message={this.getCoachMessage()}
                  speech={speechProps}
                  muteReader={this.muteReader}
                  unMuteReader={this.unMuteReader}
                  isReaderActive={this.state.isReaderActive}
                  transcript={!this.state.isReaderActive ? transcript : {}}
                  isCoachReady={this.state.coachReady}
                  isAppReady={this.state.ready}
                  setCoachReady={this.coachReadyCallback}
                  setCoachMessage={this.setCoachMessage}
                  avatarSpeechProps={speechProps.avatarSpeechProps}
                  webglProps={this.props.webglProps} // passed in through appContainer wrapped in <WebGLContext>
                  isHidden={!this.useCoach}
                />
                {this.state.ready && this.state.coachReady && <SpeechButton speech={speechProps} />}
                {this.useMenu && this.getMenu(speechProps)}
                {this.useNav && this.getNav()}
              </div>}

              <div className={readerContainerClasses}>
                <div className={readerClasses}>
                  <Route path='/student/pre-checks/asr' render={ (props) => <StudentASRReaderWrapper history={this.props.history} initialUserInteraction={this.state.initialUserInteraction} config={this.props.config} getReader={(props) => this.getReader(props, speechProps, transcript)}/> }/>
                  <Route path='/student/pre-checks' render={ (props) => <StudentPreChecks history={this.props.history} initialUserInteraction={this.state.initialUserInteraction} speechProps={speechProps} onPreCheckDone={this.onPreCheckDone} forcedAssessment={this.state.forcedAssessment} toggleReaderContainer={this.toggleReaderContainer} readyModuleTestMode={this.state.readyModuleTestMode} screenInvisible={this.state.hideReaderContainer}/>} />
                  {this.state.modal}
                  {this.state.ready &&
                    <Fragment>
                      <ForcedAssessment forcedAssessment={this.state.forcedAssessment} userType={userType}>
                        <Route path='/student/assessment' render={(props) => this.getReader(props, speechProps, transcript, config.assessment)} />
                        <Route render={(props) => <Redirect to='/student/assessment' {...props} />} />
                      </ForcedAssessment>
                      <StudentRoutes forcedAssessment={this.state.forcedAssessment} userType={userType}>
                        <Route path='/student/home' render={(props) => this.renderStudentHome(props, speechProps)} />
                        <Route path='/student/tutor' render={(props) => this.getReader(props, speechProps, transcript)} />
                        <Route path='/student/test' render={(props) => this.getReader(props, speechProps, transcript, config.studentTest)} />
                        <Route path='/student/assessment' render={(props) => this.getReader(props, speechProps, transcript, config.assessment)} />
                        <Route path='/student/readAloud' render={(props) => this.getReader(props, speechProps, null, config.readAloud)} />
                        <Route path='/student/reports/:reportType/:reportId' render={(props) => this.renderStudentHistory(props,speechProps)} />
                        <Route render={(props) => <Redirect to='/student/home' {...props} />} />
                      </StudentRoutes>
                      <TeacherRoutes userType={userType} openModal={this.openModal} closeModal={this.closeModal}/>
                      <SchoolAdminRoutes userType={userType} openModal={this.openModal} closeModal={this.closeModal} />
                      <DistrictAdminRoutes userType={userType} openModal={this.openModal} closeModal={this.closeModal} />
                    </Fragment>
                  }
                </div>
              </div>
              {userType !== userTypes.STUDENT && (!branding || branding.toUpperCase() !== "EPS") && <ChatSupport/>}
              {userType === userTypes.TEACHER && partner !== "HMH" && <EmailCaptureModal/>}
            </div>
          </Fragment>
        }
      </Speech>
    );
  }
}

App.propTypes = {
  location: PropTypes.object.isRequired
}

export default withRouter(App);